import React, { useState } from "react";
import { ChevronRight, Cigarette, Leaf, Pill } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SMOKING_OPTIONS = [
  { id: "never", label: "Never", description: "I don't smoke" },
  { id: "trying_to_quit", label: "Trying to Quit", description: "Working on it" },
  { id: "socially", label: "Socially", description: "Occasionally" },
  { id: "regularly", label: "Regularly", description: "Daily smoker" },
];

const CANNABIS_OPTIONS = [
  { id: "never", label: "Never", description: "I don't use cannabis" },
  { id: "medical_only", label: "Medical only", description: "With prescription" },
  { id: "socially", label: "Socially", description: "Occasionally, where legal" },
  { id: "prefer_not_to_say", label: "Prefer not to say", description: "Private" },
];

const MAT_OPTIONS = [
  { 
    id: "yes", 
    label: "Yes, I support MAT", 
    description: "Open to partners on medication-assisted treatment (Suboxone, Methadone, Vivitrol, etc.)" 
  },
  { 
    id: "no", 
    label: "Prefer abstinence-only", 
    description: "Prefer partners not using MAT" 
  },
  { 
    id: "prefer_not_to_say", 
    label: "No preference", 
    description: "This isn't a deciding factor for me" 
  },
];

const SubstancesStep = ({ onNext }) => {
  const [smoking, setSmoking] = useState("");
  const [cannabis, setCannabis] = useState("");
  const [matFriendly, setMatFriendly] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await api.submitSubstances({
        smoking: smoking || "prefer_not_to_say",
        cannabis: cannabis || "prefer_not_to_say",
        mat_friendly: matFriendly || "prefer_not_to_say"
      });
      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Lifestyle Choices</h2>
      <p className="text-gray-600 mb-6">Help us find compatible matches. All answers are optional.</p>

      {/* Smoking */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Cigarette className="w-4 h-4" />
          Smoking
        </label>
        <div className="grid grid-cols-2 gap-2">
          {SMOKING_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setSmoking(option.id)}
              className={`px-3 py-3 rounded-xl border-2 text-left transition-all ${
                smoking === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`font-medium text-sm ${smoking === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {option.label}
              </p>
              <p className="text-gray-500 text-xs">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Cannabis */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Leaf className="w-4 h-4" />
          Cannabis
        </label>
        <div className="bg-amber-50 rounded-lg p-3 mb-3">
          <p className="text-xs text-amber-700">
            Views on cannabis vary in the recovery community. There's no judgment here.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {CANNABIS_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setCannabis(option.id)}
              className={`px-3 py-3 rounded-xl border-2 text-left transition-all ${
                cannabis === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`font-medium text-sm ${cannabis === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {option.label}
              </p>
              <p className="text-gray-500 text-xs">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* MAT Friendly */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Pill className="w-4 h-4" />
          MAT (Medication-Assisted Treatment)
        </label>
        <div className="bg-blue-50 rounded-lg p-3 mb-3">
          <p className="text-xs text-blue-700">
            Are you open to matching with someone using medication-assisted treatment like Suboxone, Methadone, or Vivitrol?
          </p>
        </div>
        <div className="space-y-2">
          {MAT_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setMatFriendly(option.id)}
              className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                matFriendly === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`font-medium ${matFriendly === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {option.label}
              </p>
              <p className="text-gray-500 text-sm">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default SubstancesStep;
